import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import TemplateLayout from '../../components/templates/TemplateLayout';
import StepIndicator from '../../components/templates/StepIndicator';
import { get } from '../../services/api/apiClient';
import { LoadingButton, PillButton } from '../../components/ui/Button';
import { useToast } from '../../components/ui';

function AIToolRecommendationPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [mustHaveTools, setMustHaveTools] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState('random');
  const [toolCount, setToolCount] = useState(10);
  const [generatedPost, setGeneratedPost] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [toolsPoolData, setToolsPoolData] = useState({ scenarios: [] });
  const [categorizedHooksData, setCategorizedHooksData] = useState({ categories: [] });
  const [isLoading, setIsLoading] = useState(true);

  // 從 Backend API 載入敏感資料
  useEffect(() => {
    const loadData = async () => {
      try {
        const [toolsData, hooksData] = await Promise.all([
          get('/prompt-data/ai-tools'),
          get('/prompt-data/categorized-hooks')
        ]);

        if (toolsData.success) {
          setToolsPoolData(toolsData.data);
        }
        if (hooksData.success) {
          setCategorizedHooksData(hooksData.data);
        }
      } catch (error) {
        console.error('載入資料失敗:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const scenarios = toolsPoolData.scenarios || [];

  // Get available tools based on selected scenario
  const getAvailableTools = () => {
    let toolsToShow = [];

    if (selectedScenario && selectedScenario !== 'random') {
      const currentScenario = scenarios.find(s => s.name === selectedScenario);
      if (currentScenario) {
        toolsToShow = currentScenario.tools;
      }
    } else {
      const allToolsMap = new Map();
      scenarios.forEach(scenario => {
        scenario.tools.forEach(tool => {
          if (!allToolsMap.has(tool.name)) {
            allToolsMap.set(tool.name, tool);
          }
        });
      });
      toolsToShow = Array.from(allToolsMap.values());
    }

    // Separate ViralArc and sort others
    const viralArc = toolsToShow.find(tool => tool.name === 'ViralArc');
    const otherTools = toolsToShow
      .filter(tool => tool.name !== 'ViralArc')
      .sort((a, b) => a.name.localeCompare(b.name));

    return viralArc ? [viralArc, ...otherTools] : otherTools;
  };

  const handleToolToggle = (toolName) => {
    if (mustHaveTools.includes(toolName)) {
      setMustHaveTools(mustHaveTools.filter(t => t !== toolName));
    } else {
      setMustHaveTools([...mustHaveTools, toolName]);
    }
  };

  const handleScenarioChange = (scenario) => {
    setSelectedScenario(scenario);

    // Filter must-have tools if switching to specific scenario
    if (scenario && scenario !== 'random') {
      const currentScenario = scenarios.find(s => s.name === scenario);
      if (currentScenario) {
        const availableToolNames = currentScenario.tools.map(t => t.name);
        setMustHaveTools(mustHaveTools.filter(toolName =>
          availableToolNames.includes(toolName)
        ));
      }
    }
  };

  const generatePost = () => {
    setIsGenerating(true);
    try {
      let selectedTools = [];

      // Add must-have tools first
      const mustHaveSelected = [];
      scenarios.forEach(scenario => {
        scenario.tools.forEach(tool => {
          if (mustHaveTools.includes(tool.name)) {
            mustHaveSelected.push({
              name: tool.name,
              description: tool.descriptions[Math.floor(Math.random() * tool.descriptions.length)]
            });
          }
        });
      });

      // Calculate remaining tools needed
      const remainingCount = toolCount - mustHaveSelected.length;

      if (selectedScenario === 'random' || !selectedScenario) {
        // Random selection
        const allTools = [];
        scenarios.forEach(scenario => {
          scenario.tools.forEach(tool => {
            if (!mustHaveTools.includes(tool.name)) {
              allTools.push({
                name: tool.name,
                description: tool.descriptions[Math.floor(Math.random() * tool.descriptions.length)]
              });
            }
          });
        });

        const shuffled = allTools.sort(() => 0.5 - Math.random());
        selectedTools = [...mustHaveSelected, ...shuffled.slice(0, Math.max(0, remainingCount))];
      } else {
        // Scenario-based selection
        const scenario = scenarios.find(s => s.name === selectedScenario);
        if (scenario) {
          const availableTools = scenario.tools.filter(tool => !mustHaveTools.includes(tool.name));
          const shuffled = [...availableTools].sort(() => 0.5 - Math.random());
          const randomTools = shuffled.slice(0, Math.max(0, remainingCount)).map(tool => ({
            name: tool.name,
            description: tool.descriptions[Math.floor(Math.random() * tool.descriptions.length)]
          }));
          selectedTools = [...mustHaveSelected, ...randomTools];
        }
      }

      // Generate hook text
      let hookText = '';
      const categories = categorizedHooksData.categories || [];
      const allHooks = categories.flatMap(cat => cat.hooks);
      if (allHooks.length > 0) {
        const randomIndex = Math.floor(Math.random() * allHooks.length);
        hookText = allHooks[randomIndex];
      } else {
        hookText = '收好這些 AI 工具，讓你效率翻倍！';
      }

      // Generate content
      let content = hookText + '\n\n';

      // Random format style
      const formatStyles = [
        { prefix: (i) => String.fromCharCode(9312 + i), separator: '＝' },
        { prefix: (i) => String.fromCharCode(9312 + i), separator: '→' },
        { prefix: (i) => String.fromCharCode(9312 + i), separator: '｜' },
        { prefix: (i) => `${i + 1}.`, separator: '→' },
        { prefix: (i) => `${i + 1}/`, separator: '＝' },
        { prefix: (i) => `${i + 1}）`, separator: '｜' },
        { prefix: (i) => `▸${i + 1}`, separator: '→' },
        { prefix: (i) => `✦${i + 1}`, separator: '＝' },
        { prefix: (i) => `『${i + 1}』`, separator: '→' },
        { prefix: (i) => `【${i + 1}】`, separator: '＝' },
        { prefix: (i) => `〔${i + 1}〕`, separator: '｜' },
        { prefix: (i) => `⟫ ${i + 1}`, separator: '→' },
        { prefix: (i) => `➤ ${i + 1}`, separator: '＝' }
      ];

      const selectedFormat = formatStyles[Math.floor(Math.random() * formatStyles.length)];

      selectedTools.forEach((tool, index) => {
        const displayName = tool.name
          .replace('.ai', '')
          .replace('.com', '')
          .replace('.app', '')
          .replace('.io', '')
          .replace('/', ' ')
          .split(/[.\-_]/)
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

        content += `${selectedFormat.prefix(index)} ${tool.description}${selectedFormat.separator}${displayName}\n`;
      });

      const post = {
        content,
        likes: 0,
        comments: 1
      };

      setGeneratedPost(post);
    } catch (error) {
      console.error('生成失敗:', error);
      toast.error('生成失敗，請稍後再試');
    } finally {
      setIsGenerating(false);
    }
  };

  const availableTools = getAvailableTools();

  // Left Panel Content
  const leftPanel = (
    <>
      {/* Step 1: Must Have Tools */}
      <StepIndicator number={1} title="Must Have Tools (選填)">
        <p className="m-0 mb-4 text-xs text-slate-500 leading-relaxed">
          選擇你想要包含在推薦清單中的工具
        </p>
        <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto p-2 bg-gray-50 rounded-lg">
          {availableTools.map((tool) => (
            <label
              key={tool.name}
              className="flex items-center gap-2 p-2 cursor-pointer text-xs text-slate-800"
            >
              <input
                type="checkbox"
                checked={mustHaveTools.includes(tool.name)}
                onChange={() => handleToolToggle(tool.name)}
                className="w-4 h-4 cursor-pointer"
              />
              <span>{tool.name}</span>
            </label>
          ))}
        </div>
      </StepIndicator>

      {/* Step 2: Select Scenario */}
      <StepIndicator number={2} title="Select Scenario">
        <p className="m-0 mb-4 text-xs text-slate-500 leading-relaxed">
          選擇目標受眾場景，或隨機選擇
        </p>
        <select
          value={selectedScenario}
          onChange={(e) => handleScenarioChange(e.target.value)}
          className="w-full py-3 px-3 border border-gray-200 rounded-lg text-xs text-slate-800 bg-white cursor-pointer"
        >
          <option value="random">🎲 隨機選擇</option>
          {scenarios.map((scenario) => (
            <option key={scenario.name} value={scenario.name}>
              {scenario.emoji} {scenario.name}
            </option>
          ))}
        </select>
      </StepIndicator>

      {/* Step 3: Tool Count */}
      <StepIndicator number={3} title="Tool Count" marginBottom="2rem">
        <p className="m-0 mb-4 text-xs text-slate-500 leading-relaxed">
          選擇要生成的工具數量 (5-15個)
        </p>
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex justify-between items-center mb-3">
            <span className="text-xs text-slate-500">數量</span>
            <span className="text-xl font-bold text-blue-500">
              {toolCount}
            </span>
          </div>
          <input
            type="range"
            min="5"
            max="15"
            value={toolCount}
            onChange={(e) => setToolCount(parseInt(e.target.value))}
            className="w-full h-1.5 rounded cursor-pointer"
          />
          <div className="flex justify-between mt-2 text-[10px] text-gray-400">
            <span>5</span>
            <span>15</span>
          </div>
        </div>
      </StepIndicator>

      {/* Generate Button */}
      <LoadingButton
        onClick={generatePost}
        loading={isGenerating}
        loadingText="Generating..."
        icon="mdi:sparkles"
        variant="accent"
        size="md"
        className="w-full"
      >
        Generate AI Tool List
      </LoadingButton>
    </>
  );

  // Middle Panel Content - Mobile Preview
  const middlePanel = (
    <div className="w-full h-full flex flex-col items-center justify-center p-8 bg-gray-50">
      <div className="flex gap-4 mb-8">
        <PillButton selected={true}>
          Preview
        </PillButton>
        <PillButton
          onClick={() => navigate('/workflow-builder?template=ai-tool-recommendation')}
        >
          Go Live
        </PillButton>
      </div>

      {/* Mobile Preview */}
      <div className="w-[375px] h-[667px] bg-black rounded-[40px] p-3 shadow-[0_20px_60px_rgba(0,0,0,0.3)] relative overflow-hidden">
        <div className="w-full h-full bg-black rounded-[32px] overflow-hidden flex flex-col">
          {generatedPost ? (
            <>
              {/* Top Status Bar */}
              <div className="py-2 px-4 flex items-center justify-between text-white text-xs">
                <span>2:03</span>
                <div className="flex gap-1 items-center">
                  <Icon icon="mdi:signal" className="text-xs" />
                  <Icon icon="mdi:wifi" className="text-xs" />
                  <Icon icon="mdi:battery" className="text-xs" />
                </div>
              </div>

              {/* Page Header */}
              <div className="py-3 px-4 flex items-center justify-between border-b border-neutral-800">
                <div className="flex items-center gap-4 flex-1">
                  <Icon icon="mdi:arrow-left" className="text-xl text-white" />
                  <div className="text-center flex-1">
                    <div className="text-neutral-400 text-[10px]">viralarc.ai</div>
                    <div className="text-white text-xs font-semibold">Posts</div>
                  </div>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-4">
                {/* Post Header */}
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-500 flex items-center justify-center text-xs text-white font-semibold flex-shrink-0">
                    V
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-white text-xs font-semibold">
                        viralarc.ai
                      </span>
                      <span className="w-1 h-1 rounded-full bg-neutral-400"></span>
                      <span className="text-neutral-400 text-xs">
                        Just now
                      </span>
                    </div>

                    {/* Post Content */}
                    <div className="text-white text-xs leading-relaxed whitespace-pre-wrap mt-2">
                      {generatedPost.content}
                    </div>
                  </div>
                </div>

                {/* Post Actions */}
                <div className="flex items-center gap-6 py-3 border-t border-neutral-800">
                  <div className="flex items-center gap-2">
                    <Icon icon="mdi:heart-outline" className="text-base text-neutral-400" />
                    <span className="text-neutral-400 text-xs">
                      {generatedPost.likes || 0}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon icon="mdi:comment-outline" className="text-base text-neutral-400" />
                    <span className="text-neutral-400 text-xs">
                      {generatedPost.comments || 0}
                    </span>
                  </div>
                  <Icon icon="mdi:share-outline" className="text-base text-neutral-400" />
                  <Icon icon="mdi:send-outline" className="text-base text-neutral-400 ml-auto" />
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-neutral-400 text-xs text-center p-8">
              Generate your AI tool list to see preview
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Build thinking messages stream (ChatGPT style)
  const buildThinkingMessages = () => {
    const messages = [];

    // Stage 1: Tool Selection
    if (isGenerating) {
      messages.push({
        id: 'select-tools',
        type: 'thinking',
        text: ' Selecting AI tools...'
      });
      if (mustHaveTools.length > 0) {
        messages.push({
          id: 'must-have',
          type: 'thinking',
          text: `→ Including ${mustHaveTools.length} must-have tools`
        });
      }
      if (selectedScenario !== 'random') {
        messages.push({
          id: 'scenario',
          type: 'thinking',
          text: `→ Filtering by scenario: ${selectedScenario}`
        });
      }
      messages.push({
        id: 'random-select',
        type: 'thinking',
        text: `→ Randomly selecting ${toolCount} tools`
      });
    }

    // Stage 2: Hook Selection
    if (isGenerating) {
      messages.push({
        id: 'select-hook',
        type: 'thinking',
        text: '🎣 Choosing engaging hook...'
      });
      messages.push({
        id: 'hook-detail',
        type: 'thinking',
        text: '→ Selecting from categorized hook library'
      });
    }

    // Stage 3: Format Selection
    if (isGenerating) {
      messages.push({
        id: 'select-format',
        type: 'thinking',
        text: '✨ Applying random format style...'
      });
      messages.push({
        id: 'format-detail',
        type: 'thinking',
        text: '→ Choosing from 13 different formatting styles'
      });
    }

    // Stage 4: Content Generation
    if (isGenerating) {
      messages.push({
        id: 'gen-content',
        type: 'thinking',
        text: '🤖 Generating tool list...'
      });
      messages.push({
        id: 'gen-descriptions',
        type: 'thinking',
        text: '→ Adding tool descriptions'
      });
      messages.push({
        id: 'gen-format',
        type: 'thinking',
        text: '→ Formatting for Threads platform'
      });
    } else if (generatedPost) {
      messages.push({
        id: 'gen-complete',
        type: 'completed',
        text: '✨ AI tool list generated successfully!'
      });
      messages.push({
        id: 'gen-ready',
        type: 'completed',
        text: `→ Ready to share ${toolCount} curated tools`
      });
    }

    return messages;
  };

  const thinkingMessages = buildThinkingMessages();

  // Right Panel Content - Thinking Stream (ChatGPT style)
  const rightPanel = (
    <div className="h-full flex flex-col bg-gray-50 p-8 overflow-y-auto">
      {thinkingMessages.length > 0 ? (
        <div className="flex flex-col gap-3">
          <h3 className="m-0 mb-4 text-sm font-semibold text-slate-800 flex items-center gap-2">
            <Icon icon="mdi:brain" className="text-base text-blue-500" />
            Thinking Process
          </h3>

          {thinkingMessages.map((message, index) => (
            <div
              key={message.id}
              className="flex items-start gap-3 py-3 px-4 bg-white rounded-lg border border-gray-200 animate-[fadeInUp_0.3s_ease-out_both]"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              {/* Status Icon */}
              <div className="min-w-[20px] h-5 flex items-center justify-center">
                {message.type === 'thinking' && (
                  <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                )}
                {message.type === 'completed' && (
                  <div className="text-blue-500 text-xs font-bold">
                    ✓
                  </div>
                )}
              </div>

              {/* Message Text */}
              <div className={`flex-1 text-xs leading-relaxed ${
                message.type === 'thinking' ? 'text-slate-600 font-normal' : 'text-slate-800 font-medium'
              }`}>
                {message.text}
              </div>
            </div>
          ))}

          {/* Thinking Indicator at the end if still processing */}
          {isGenerating && (
            <div className="flex items-center gap-2 py-3 px-4 text-slate-400 text-xs">
              <div className="flex gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '-0.32s' }} />
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '-0.16s' }} />
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" />
              </div>
              <span>Thinking...</span>
            </div>
          )}
        </div>
      ) : (
        // Empty State
        <div className="flex-1 flex flex-col items-center justify-center text-center text-slate-400">
          <Icon icon="mdi:robot-happy-outline" className="text-5xl mb-4" />
          <p className="m-0 text-xs text-slate-500">
            Click generate to see the thinking process
          </p>
          <p className="mt-2 text-xs text-slate-400">
            Watch AI curate your tool list in real-time
          </p>
        </div>
      )}
    </div>
  );

  return (
    <>
      <TemplateLayout
        title="AI Tool Recommendation"
        subtitle="Generate shareable AI tool lists with one click"
        leftPanel={leftPanel}
        middlePanel={middlePanel}
        rightPanel={rightPanel}
        backPath="/projects"
      />
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </>
  );
}

export default AIToolRecommendationPage;
